import { useCallback, useMemo } from 'react'
import { useDispatch } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { addToCart, updateCartItemQuantity } from '../../actions/cart'
import { RESOURCE_RESTRAINED_HARDWARE } from '../../VNMode/Utils'
import { bridge_getDeviceModel } from '../../VNAndroidSDK/bridgeCalls/VNWebSDKDataSend'
import { useCartProvider } from './VNCartContext'

const THROTTLING_TIME = 70 // in milliseconds
const THROTTLING_LONGER_TIME = 125

/**
 * Custom hook for handling cart item actions with throttling.
 * Relies on useRef for throttling and tracking state without additional renders.
 *
 * @param {Object} param - The parameters object.
 * @param {Object} param.throttlingRef - tracks the throttling timer
 * @param {Object} param.throttlingActiveRef - tracks if throttling is active
 * @param {Object} param.isInCartRef - tracks if the item is in the cart.
 * @param {Object} param.quantitiesRef - tracks the quantity of each item in cart.
 * @returns {Object}
 */
const useCartItem = ({ throttlingRef, throttlingActiveRef, isInCartRef = null, quantitiesRef = null }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const isResourceRestrained = useMemo(() => RESOURCE_RESTRAINED_HARDWARE?.includes(bridge_getDeviceModel()), [])
  const { isCartLocked } = useCartProvider()

  /**
   * Add item to cart with required modifiers or open price
   * this adds the item to cart, then opens Item Details
   */
  const addNonRegularItem = useCallback(
    ({ itemId }) => {
      history.push({ pathname: `/concession-order/item-detail/${itemId}` })
    },
    [history],
  )

  /**
   * Add or increment item in cart with throttling for consecutive taps
   * this updates refs to avoid having to wait for state updates
   */
  const upsertItem = useCallback(
    ({
      itemId,
      max,
      isAlcohol,
      cartIndex,
      hasRequiredModifiers = false,
      defaultModifiers = [],
      updateLocalQuantities = () => {},
      isAlreadyInCart = null,
      quantity = 1,
    }) => {
      const isInCart = isInCartRef?.current || isAlreadyInCart
      const curQuantity = quantitiesRef?.current ?? quantity
      if (isCartLocked()) {
        updateLocalQuantities()
        return
      }

      // another tap is registered, clear timer
      if (throttlingRef?.current?.[itemId]) {
        clearTimeout(throttlingRef?.current?.[itemId])
      }

      // start a new timer
      throttlingActiveRef.current[itemId] = true
      throttlingRef.current[itemId] = setTimeout(
        () => {
          if (isCartLocked()) {
            updateLocalQuantities()
            return
          }
          throttlingActiveRef.current[itemId] = false

          // has modifiers
          if (hasRequiredModifiers) return

          // add to cart
          if (!isInCart && cartIndex === -1) {
            if (curQuantity === 0) {
              return
            }
            dispatch(addToCart({ itemId, modifiers: defaultModifiers, quantity: curQuantity, isAlcohol }))
            if (isAlreadyInCart === null) {
              isInCartRef.current = true
            }
            return
          }

          // increment quantity
          if (curQuantity >= max) {
            dispatch(updateCartItemQuantity({ index: cartIndex, quantity: max }))
            return
          }

          dispatch(updateCartItemQuantity({ index: cartIndex, quantity: curQuantity }))
          return
        },
        isResourceRestrained ? THROTTLING_LONGER_TIME : THROTTLING_TIME,
      )
    },
    [dispatch, isInCartRef, isResourceRestrained, quantitiesRef, throttlingActiveRef, throttlingRef, isCartLocked],
  )

  return { addNonRegularItem, upsertItem }
}

export default useCartItem
